import { useDocumentData } from "react-firebase-hooks/firestore";
import { useTranslation } from "react-i18next";
import { Badge } from "components/ui/badge";
import { Text } from "components/text";
import { getSemanticDataDocument } from "integrations/firebase/collections/semanticData";
import { ManuscriptProcessingRow } from "./ManuscriptProcessingRow";

const MetadataRow = ({ label, value }: { label: string; value: string }) => (
  <ManuscriptProcessingRow label={label}>
    <Text variant="body" className="min-w-0 truncate">
      {value}
    </Text>
  </ManuscriptProcessingRow>
);

// Read by id, so an entry opens the result it produced rather than the latest.
export const ManuscriptProcessingSummary = ({
  semanticDataId,
}: {
  semanticDataId: string;
}) => {
  const { t } = useTranslation("settings");
  const [semanticData, isLoading, error] = useDocumentData(
    getSemanticDataDocument(semanticDataId),
  );

  if (isLoading) {
    return (
      <Text variant="subtle" className="pt-2">
        {t("organisation.manuscriptProcessing.sidebar.summaryLoading")}
      </Text>
    );
  }

  if (error || !semanticData) {
    return (
      <Text variant="subtle" className="pt-2">
        {t("organisation.manuscriptProcessing.sidebar.summaryUnavailable")}
      </Text>
    );
  }

  const { synopsis, genres, metadata } = semanticData;
  const hasMetadata = Boolean(
    metadata?.title || metadata?.author || metadata?.language,
  );

  return (
    <div className="flex flex-col gap-4 pt-2">
      {synopsis && (
        <Text variant="body" className="text-pretty">
          {synopsis}
        </Text>
      )}

      {genres && genres.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {genres.map((genre) => (
            <Badge key={genre} variant="secondary">
              {genre}
            </Badge>
          ))}
        </div>
      )}

      {hasMetadata && (
        <div>
          {metadata?.title && (
            <MetadataRow
              label={t("organisation.manuscriptProcessing.sidebar.title")}
              value={metadata.title}
            />
          )}
          {metadata?.author && (
            <MetadataRow
              label={t("organisation.manuscriptProcessing.sidebar.author")}
              value={metadata.author}
            />
          )}
          {metadata?.language && (
            <MetadataRow
              label={t("organisation.manuscriptProcessing.sidebar.language")}
              value={metadata.language}
            />
          )}
        </div>
      )}

      {!synopsis && !genres?.length && !hasMetadata && (
        <Text variant="subtle">
          {t("organisation.manuscriptProcessing.sidebar.summaryEmpty")}
        </Text>
      )}
    </div>
  );
};
